import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Session glissante (décision de Mélilia du 02/10) : on reste connecté tant qu'on agit ; sans aucune action pendant
// 2 heures, la session coupe et l'écran revient à la connexion. Un geste de la personne (clic, touche, défilement,
// mouvement) renouvelle la session, au plus toutes les 5 minutes. Les appels que la page fait seule (cloche,
// rafraîchissements) ne renouvellent rien : un ordinateur oublié ouvert se referme seul.
//
// Depuis le 16/10 (audit de sécurité), **le jeton ne vit plus dans le navigateur** : il voyage dans le cookie
// « access_token », inaccessible aux scripts, que le navigateur envoie seul. Un script hostile ne peut donc plus
// l'emporter. Ce qui reste ici n'est pas un secret : l'heure de début de session et sa durée, pour la minuterie
// d'inactivité. Les durées se mesurent à l'horloge de cet ordinateur (comparer à celle du serveur déconnecterait
// sur-le-champ un poste décalé) ; le serveur reste de toute façon le juge : il refuse un jeton expiré.
// Plusieurs onglets partagent le cookie et ces repères : un geste dans l'un garde les autres ouverts.
const SESSION_AT_KEY = "metara_session_at";
const SESSION_TTL_KEY = "metara_session_ttl";
const DEFAULT_TTL_MS = 2 * 60 * 60 * 1000;
const RENEW_AFTER_MS = 5 * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 15 * 1000;
const CHECK_EVERY_MS = 30 * 1000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "mousemove"];
const read = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k, v) => { try { localStorage.setItem(k, v); } catch { /* navigation privée : la minuterie repart à zéro */ } };
const saveSession = (expiresInSeconds) => {
  write(SESSION_AT_KEY, String(Date.now()));
  write(SESSION_TTL_KEY, String((Number(expiresInSeconds) || 0) * 1000 || DEFAULT_TTL_MS));
};
const clearSession = () => {
  try { localStorage.removeItem(SESSION_AT_KEY); localStorage.removeItem(SESSION_TTL_KEY); } catch { /* ignore */ }
};
const hasSessionMark = () => !!read(SESSION_AT_KEY);
const sessionAgeMs = () => Date.now() - Number(read(SESSION_AT_KEY) || 0);
const sessionTtlMs = () => Number(read(SESSION_TTL_KEY) || 0) || DEFAULT_TTL_MS;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const resyncing = useRef(false);

  // Au démarrage, on demande toujours « qui suis-je ? » : le site ne peut plus lire le cookie, c'est le serveur qui
  // dit si la session tient. Le cookie est donc la seule source de vérité — un navigateur dont le stockage local a
  // été vidé reste connecté. Un visiteur anonyme reçoit un 401 ici : c'est la réponse normale à la question, pas une
  // erreur. Si la session est ouverte sans repère local (stockage vidé), on en pose un : la minuterie d'inactivité
  // en a besoin, et le serveur reste de toute façon le juge de l'expiration réelle.
  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        if (!hasSessionMark()) saveSession(DEFAULT_TTL_MS / 1000);
      })
      .catch(clearSession)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onExpired = () => {
      clearSession();
      setUser((prev) => {
        if (prev) toast.error("Session expirée, veuillez vous reconnecter.");
        return null;
      });
    };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let lastSeen = 0;
    let renewing = false;
    const renewIfDue = async () => {
      if (!hasSessionMark() || renewing || sessionAgeMs() < RENEW_AFTER_MS) return;
      renewing = true;
      try {
        const { data } = await api.post("/auth/refresh");
        saveSession(data.expires_in);
      } catch { /* session déjà expirée : l'intercepteur de lib/api.js la coupe */ }
      finally { renewing = false; }
    };
    const onActivity = () => {
      const t = Date.now();
      if (t - lastSeen < ACTIVITY_THROTTLE_MS) return;
      lastSeen = t;
      renewIfDue();
    };
    const checkExpiry = () => {
      // Durée annoncée par le serveur à la connexion (expires_in) ; le serveur refuse de toute façon un jeton expiré.
      if (!hasSessionMark() || sessionAgeMs() >= sessionTtlMs()) {
        clearSession();
        window.dispatchEvent(new Event("auth:expired"));
      }
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const timer = setInterval(checkExpiry, CHECK_EVERY_MS);
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(timer);
    };
  }, [user]);

  useEffect(() => {
    // Plusieurs appels peuvent recevoir ONBOARDING_REQUIRED en parallèle
    // (ex. plusieurs widgets d'une même page) — un seul refetch à la fois.
    const onOnboardingRequired = () => {
      if (resyncing.current) return;
      resyncing.current = true;
      api.get("/auth/me")
        .then((r) => setUser(r.data))
        .catch(() => {})
        .finally(() => { resyncing.current = false; });
    };
    window.addEventListener("auth:onboarding-required", onOnboardingRequired);
    return () => window.removeEventListener("auth:onboarding-required", onOnboardingRequired);
  }, []);

  const login = async (email, password, turnstileToken) => {
    clearSession();
    const { data } = await api.post("/auth/login", { email, password, turnstile_token: turnstileToken || undefined });
    saveSession(data.expires_in);  // le jeton de la réponse n'est pas conservé : le cookie porte la session
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearSession();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
